import { useState } from 'react';

import { Menu, Typography } from '@/ui';

import type { FC } from 'react';

interface NumberRangeFilterProps {
  label: string;
  onChange: (range: [number | undefined, number | undefined]) => void;
  value: [number | undefined, number | undefined];
}

const NumberRangeFilter: FC<NumberRangeFilterProps> = ({
  label,
  onChange,
  value,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const hasValue = value[0] !== undefined || value[1] !== undefined;
  const displayValue = hasValue ? `${value[0] ?? '0'}-${value[1] ?? '∞'}` : '';
  const maxInputId = `${label.toLowerCase()}-max-filter`;
  const minInputId = `${label.toLowerCase()}-min-filter`;
  const toNumber = (raw: string) => {
    if (raw === '') return undefined;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : undefined;
  };

  return (
    <>
      <button
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
          hasValue ? 'bg-sage-100 text-sage-600' : 'bg-white text-cement-500'
        }`}
        style={{ border: '1px solid #A4A4A4' }}
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {label}
        {hasValue && <span className="text-sage-500">({displayValue})</span>}
        <span className="material-icons text-base">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={open}
        slotProps={{
          paper: {
            style: {
              borderRadius: '8px',
              marginTop: 4,
              minWidth: 240,
              padding: '12px 0 4px',
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setAnchorEl(null)}
      >
        <div className="px-4">
          <div className="mb-2 flex items-center justify-between">
            <Typography className="!text-cement-500" variant="caption">
              Range
            </Typography>
            {hasValue && (
              <button
                className="text-xs text-cement-400 underline hover:text-cement-600"
                type="button"
                onClick={() => {
                  onChange([undefined, undefined]);
                  setAnchorEl(null);
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="flex-1" htmlFor={minInputId}>
              <span className="mb-0.5 block text-[10px] text-cement-400">
                Min
              </span>
              <input
                className="w-full rounded border border-cement-300 px-2 py-1.5 text-sm outline-none focus:border-sage-500"
                id={minInputId}
                min={0}
                type="number"
                value={value[0] ?? ''}
                onChange={(e) => onChange([toNumber(e.target.value), value[1]])}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </label>
            <label className="flex-1" htmlFor={maxInputId}>
              <span className="mb-0.5 block text-[10px] text-cement-400">
                Max
              </span>
              <input
                className="w-full rounded border border-cement-300 px-2 py-1.5 text-sm outline-none focus:border-sage-500"
                id={maxInputId}
                min={0}
                type="number"
                value={value[1] ?? ''}
                onChange={(e) => onChange([value[0], toNumber(e.target.value)])}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </label>
          </div>
        </div>
      </Menu>
    </>
  );
};

export default NumberRangeFilter;
